import { CssBaseline } from '@mui/material'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { QueryClient, QueryClientProvider } from 'react-query'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { SnackbarProvider } from 'notistack'
import ResetPassword from './pages/ResetPassword'
import Layout from './components/ui/Layout'
import {
  RequireAdmin,
  RequireAuth,
  RequireDealershipBrand,
  UserAuthProvider,
} from './hooks/context/UserAuthContext'
import Users from './pages/admin/Users'
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import WarrantyClaim from './pages/WarrantyClaim'
import WarrantyRegistration from './pages/WarrantyRegistration'
import ForgotPassword from './pages/ForgotPassword'
import CreateUser from './pages/admin/CreateUser'
import EditUser from './pages/admin/EditUser'
import PrivacyPolicy from './pages/PrivacyPolicy'
import TermsAndConditions from './pages/TermsAndConditions'
import CreateDealership from './pages/admin/CreateDealership'
import EditDealership from './pages/admin/EditDealership'
import Dealerships from './pages/admin/Dealerships'
import AdminLayout from './components/ui/admin/AdminLayout'
import WarrantyRegistrationMercedesBenz from './pages/WarrantyRegistration/WarrantyRegistrationMercedesBenz'
import WarrantyRegistrationBMW from './pages/WarrantyRegistration/WarrantyRegistrationBMW'
import WarrantyRegistrationJLR from './pages/WarrantyRegistration/WarrantyRegistrationJLR'
import WarrantyRegistrationVW from './pages/WarrantyRegistration/WarrantyRegistrationVW'
import { availableDealershipBrands } from './constants/dealershipBrands'
import WarrantyRegistrationBYD from './pages/WarrantyRegistration/WarrantyRegistrationBYD'
import WarrantyRegistrationVWSG from './pages/WarrantyRegistration/WarrantyRegistrationVWSG'

const theme = createTheme({
  overrides: {
    MuiCssBaseline: {
      '@global': {
        a: {
          color: 'inherit',
          textDecoration: 'none',
        },
      },
    },
    MuiContainer: {
      root: {
        paddingBottom: 24,
      },
    },
    MuiTableCell: {
      root: {
        overflow: 'hidden',
      },
    },
  },
  palette: {
    primary: {
      main: '#111111',
    },
    secondary: {
      main: '#FF0026',
    },
  },
})

const queryClient = new QueryClient()

function App() {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <CssBaseline />
            <SnackbarProvider>
              <UserAuthProvider>
                <Routes>
                  <Route element={<Layout />}>
                    <Route path='/login' element={<Login />} />
                    <Route path='/reset-password/:token' element={<ResetPassword />} />
                    <Route path='/forgot-password' element={<ForgotPassword />} />

                    <Route element={<RequireAuth />}>
                      <Route
                        element={
                          <RequireDealershipBrand dealershipBrands={availableDealershipBrands} />
                        }
                      >
                        <Route path='/register-warranty' element={<WarrantyRegistration />} />
                      </Route>
                    </Route>
                    <Route element={<RequireDealershipBrand dealershipBrands={['mercedes']} />}>
                      <Route path='/mercedes-benz' element={<WarrantyRegistrationMercedesBenz />} />
                    </Route>
                    <Route element={<RequireDealershipBrand dealershipBrands={['bmw']} />}>
                      <Route path='/bmw' element={<WarrantyRegistrationBMW />} />
                    </Route>
                    <Route element={<RequireDealershipBrand dealershipBrands={['jlr']} />}>
                      <Route path='/jlr' element={<WarrantyRegistrationJLR />} />
                    </Route>
                    <Route element={<RequireDealershipBrand dealershipBrands={['vw']} />}>
                      <Route path='/vw' element={<WarrantyRegistrationVW />} />
                    </Route>
                    <Route element={<RequireDealershipBrand dealershipBrands={['byd']} />}>
                      <Route path='/byd' element={<WarrantyRegistrationBYD />} />
                    </Route>
                    <Route element={<RequireDealershipBrand dealershipBrands={['vwsg']} />}>
                      <Route path='/vwsg' element={<WarrantyRegistrationVWSG />} />
                    </Route>

                    <Route path='/privacy-policy' element={<PrivacyPolicy />} />
                    <Route path='/terms-and-conditions' element={<TermsAndConditions />} />
                    <Route path='/' element={<Navigate to='/login' replace />} />

                    <Route element={<RequireAuth />}>
                      <Route path='/submit-claim/:id' element={<WarrantyClaim />} />
                      <Route path='/dashboard' element={<Dashboard />} />
                      <Route path='/admin' element={<RequireAdmin />}>
                        <Route element={<AdminLayout />}>
                          <Route path='users'>
                            <Route path='create' element={<CreateUser />} />
                            <Route path='edit/:userId' element={<EditUser />} />
                            <Route index element={<Users />} />
                          </Route>
                          <Route path='dealerships'>
                            <Route path='create' element={<CreateDealership />} />
                            <Route path='edit/:dealershipId' element={<EditDealership />} />
                            <Route index element={<Dealerships />} />
                          </Route>
                        </Route>
                        <Route index element={<Navigate to='/admin/users' replace />} />
                      </Route>
                    </Route>
                  </Route>
                </Routes>
              </UserAuthProvider>
            </SnackbarProvider>
          </LocalizationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  )
}

export default App
